// Legal pages — Privacy, Terms, Cookies. Written in plain language so a
// visitor can actually read them, while still matching how the product
// actually behaves (private rollout vs public) via the rollout flag.
import { useDocumentMeta } from "../lib/useDocumentMeta";
import { isPublic, rolloutMode } from "../rollout";
import { toDocumentMeta, getRouteMeta } from "../route-meta";

const LAST_UPDATED = "12 October 2026";

type Section = { h: string; body: string[] };

function LegalShell({
  eyebrow,
  title,
  intro,
  sections,
}: {
  eyebrow: string;
  title: string;
  intro: string;
  sections: Section[];
}) {
  return (
    <>
      <section className="mk-container pt-12 sm:pt-20 pb-6">
        <p className="mk-eyebrow">{eyebrow}</p>
        <h1 className="mk-h1 mt-4 max-w-3xl">{title}</h1>
        <p className="mk-lead mt-5 max-w-3xl">{intro}</p>
        <p className="mt-4 text-[11px] uppercase tracking-[0.2em] text-px-text-muted">
          Last updated · {LAST_UPDATED}
        </p>
      </section>

      <article className="mk-container pb-20 mk-prose max-w-3xl">
        {sections.map((s) => (
          <section key={s.h}>
            <h2>{s.h}</h2>
            {s.body.map((p, i) => (
              <p key={i}>{p}</p>
            ))}
          </section>
        ))}

        <section>
          <h2>Contact</h2>
          <p>
            Questions about any of this? Email{" "}
            <a href="mailto:jorgen@larsen.zone">jorgen@larsen.zone</a>. PX7 Training is operated by
            the individual running <a href="https://px7.digital">PX7 Digital</a>, based in
            Copenhagen, Denmark.
          </p>
        </section>
      </article>
    </>
  );
}

export function Privacy() {
  useDocumentMeta(toDocumentMeta(getRouteMeta("/privacy", rolloutMode)!));
  return (
    <LegalShell
      eyebrow="Privacy policy"
      title="How we use your data."
      intro="PX7 Training is a digital running-training platform. We collect only what we need to run the service. We don't sell data, we don't share it with advertisers, and we don't use third-party analytics or tracking."
      sections={[
        {
          h: "Newsletter",
          body: [
            "If you request the PX7 Training newsletter, we store your email, the consent wording version, the request and confirmation times, and whether you are subscribed. We send a confirmation email through Resend; your address becomes a newsletter recipient after you confirm it. We send emails about PX7 Training, the method and launch updates. You can unsubscribe using the link in each email. Existing accounts and waitlist entries are not automatically subscribed.",
            "Newsletter subscriptions are stored separately from training accounts. Subscription links contain private tokens; the management page does not load site analytics. To request removal of your newsletter record, contact jorgen@larsen.zone.",
          ],
        },
        {
          h: "What we collect",
          body: [
            "When you join the waitlist, accept an invitation, or sign up, we store your email address, name (if you provide one), and any notes you include. When you use the app, we store the things you'd expect a training app to store: your training plan, session logs, assessments, zone history, messages with your coach, and time-off records.",
            "Session cookies contain a signed JSON Web Token identifying your user ID. That's it — no tracking identifiers, no third-party cookies.",
          ],
        },
        {
          h: "What we don't collect",
          body: [
            "We don't run third-party analytics. No Google Analytics, no Meta Pixel, no Mixpanel, no heatmaps, no session replay.",
            "The marketing site loads PX7 Digital site analytics from analytics.px7.digital. We also keep first-party product telemetry — a small events table that records which step of onboarding, assessment, and plan generation you reach — so we can see whether the product is working. That data lives in our own database, never leaves it, and never gets linked to an ad profile.",
            "We don't track you across sites. We don't sell or rent data. We don't build advertising profiles.",
          ],
        },
        {
          h: "Where your data lives",
          body: [
            "All training data is stored in Cloudflare D1 (a managed SQLite database), running on Cloudflare's European infrastructure. Email delivery is handled by Resend for newsletters and transactional messages — invitations, password resets, weekly digests, and assessment reminders. The only data shared with Resend is your email address and the message itself.",
            "Payment processing, when you upgrade to a paid tier, is handled by Stripe. Stripe sees your card details, name, and billing address — we don't. Our database stores only the Stripe customer ID and the current subscription status so we know which tier you're on.",
            "Public forms can be protected by Cloudflare Turnstile, which replaces a CAPTCHA and loads from Cloudflare's challenges.cloudflare.com domain. When Turnstile is enabled, only the data needed to distinguish humans from bots is sent to Cloudflare for that check.",
          ],
        },
        {
          h: "Your rights",
          body: [
            "Under GDPR and similar frameworks, you have the right to access a copy of your data, correct anything inaccurate, object in some situations, and delete your account. The app includes self-serve data export and account deletion from the Account page.",
            "Account deletion removes your user record and cascades through plans, logs, assessments, zones, messages, and time-off records. If you want to exercise a privacy right or ask a question about your data, email us. You also have the right to complain to Datatilsynet, the Danish Data Protection Agency.",
          ],
        },
        {
          h: "Children",
          body: [
            "PX7 Training isn't designed for or directed at children under 16. Don't sign up kids without parental involvement.",
          ],
        },
        {
          h: "Changes",
          body: [
            "If this policy changes materially, we'll update the date above and notify existing users by email. Invited users will hear from us directly before anything substantive changes.",
          ],
        },
      ]}
    />
  );
}

export function Terms() {
  useDocumentMeta(toDocumentMeta(getRouteMeta("/terms", rolloutMode)!));
  return (
    <LegalShell
      eyebrow="Terms of service"
      title="The rules of using PX7 Training."
      intro="PX7 Training is an endurance-training platform. These terms are the rules you agree to by using the app, joining the waitlist, or accepting an invitation."
      sections={[
        {
          h: "What you're signing up for",
          body: [
            "Access to the PX7 Training digital running app (https://app.px7.training). The site describes the intended launch product. The full digital app is one subscription at €24/month. A written training review by Jørgen is an optional €89 one-off purchase. Prices include applicable VAT. Ongoing coaching and appointments through LØBEMENTOR are separate services.",
            isPublic
              ? "The digital app is billed monthly. The subscription details, first payment date and any trial are shown before checkout. A written review is purchased separately and does not renew."
              : "PX7 Training has not launched publicly. Selected runners can try the app free for a limited pilot period. Dates are confirmed in the invitation; continuing afterwards requires choosing a paid subscription. Launch subscriptions and prices are listed on the Pricing page. You will see the subscription details again before paid signup. Joining the waitlist does not create a paid subscription.",
          ],
        },
        {
          h: "Your responsibilities",
          body: [
            "Keep your login credentials to yourself. One account per person. If you think your account has been accessed by someone else, tell us.",
            "The method is information, not medical advice. Running is a physical activity with inherent risks. If you have a pre-existing condition, are pregnant, or have any doubt about whether training is safe for you right now, talk to a qualified medical professional before following any plan the app generates.",
            "Don't use the platform to harass, spam, or distribute anything unlawful.",
          ],
        },
        {
          h: "Our responsibilities",
          body: [
            "We make reasonable efforts to keep the service available and your data safe. We'll notify you before material changes to pricing, privacy, or features.",
            "We can't guarantee athletic outcomes. Training is long-term, individual, and affected by many things outside the platform's control. Research informs the method, but the complete PX7 Training system has not been shown to outperform a comparable training plan.",
          ],
        },
        {
          h: isPublic ? "Billing" : "Billing when paid access opens",
          body: [
            isPublic
              ? "Payments are handled by Stripe. Review the price, first payment date and any trial before checkout. Manage your subscription from the Account page."
              : "During the current private rollout, invited access is free. Paid subscriptions and written reviews are not open for purchase. Prices, payment dates and cancellation details will be shown before checkout when paid access opens.",
            "The published consumer prices include applicable VAT. Any currency conversion charged by your payment provider is separate. A written review is an optional one-off purchase, not part of the monthly subscription.",
            "Cancel your subscription before the next renewal to stop future charges. Access continues to the end of the paid period. Plan history, logs and assessments remain in your account until you delete them, subject to our Privacy Policy. Statutory consumer rights are unaffected.",
            "Existing users will receive notice before changes to their access or billing. This launch offer does not automatically change an existing agreement.",
          ],
        },
        {
          h: "Written training reviews",
          body: [
            "A review covers up to four weeks of your PX7 Training history and one main question about training or an upcoming race. Jørgen provides a written assessment and recommendations for your next sessions. One follow-up question to clarify that review is included if sent within seven days of delivery; the answer is provided within three working days.",
            "We confirm that your question fits the service and that capacity is available before payment. Delivery is within five working days of payment and receipt of the agreed information. Working days are Monday to Friday, excluding Danish public holidays. If we cannot deliver the agreed review, we offer a refund or a revised date for you to accept.",
            "New topics, later training periods, regular check-ins, calls and a fully coached programme are separate services through LØBEMENTOR. We explain this before taking payment if your request needs that level of support.",
          ],
        },
        {
          h: "Termination",
          body: [
            "You can delete your account at any time. We may suspend or terminate accounts that violate these terms (harassment, fraud, abuse of others), with notice where practical.",
          ],
        },
        {
          h: "Liability",
          body: [
            "We provide PX7 Training \"as is\" and disclaim all warranties to the extent allowed by law. In the event of a legal dispute, our liability is limited to the amount you've paid us in the previous 12 months. This doesn't exclude liability for things that can't be excluded under applicable law (e.g. gross negligence).",
          ],
        },
        {
          h: "Governing law",
          body: [
            "These terms are governed by the laws of Denmark. Any dispute will be resolved in the courts of Copenhagen.",
          ],
        },
      ]}
    />
  );
}

export function Cookies() {
  useDocumentMeta(toDocumentMeta(getRouteMeta("/cookies", rolloutMode)!));
  return (
    <LegalShell
      eyebrow="Cookies"
      title="Cookies and website services."
      intro="The app uses a first-party sign-in cookie. This page also describes site analytics, fonts and services used for payments and form protection."
      sections={[
        {
          h: "px7_session",
          body: [
            "An HTTP-only, secure, same-site cookie carrying a signed JWT session token. Set when you sign in and cleared when you sign out. Valid for 14 days before it needs renewing. Without it, the app can't tell it's you on the next request.",
            "This cookie is strictly necessary for the service to work. It isn't used for tracking and it isn't shared with anyone.",
          ],
        },
        {
          h: "What we don't use",
          body: [
            "No Google Analytics, Meta Pixel, Hotjar, Mixpanel or session replay. The marketing site loads a PX7 Digital site-analytics script from analytics.px7.digital, and fonts from Google Fonts.",
          ],
        },
        {
          h: "Third-party services that load on the app",
          body: [
            "Stripe Checkout and the Stripe customer portal load Stripe's scripts when you visit the upgrade or billing-management pages. Stripe may set their own cookies while you're on those pages — they're strictly necessary for payment to work.",
            "Cloudflare Turnstile can load on the signup, forgot-password, and club-invite pages when bot protection is enabled. Turnstile is used as a security control, not for advertising or cross-site tracking.",
            "Marketing fonts (Inter, Space Grotesk, JetBrains Mono) load from Google Fonts. Google will see your IP when fonts are fetched.",
          ],
        },
      ]}
    />
  );
}
